/**
 * SSE framing, and nothing else.
 *
 * This module has ZERO imports and touches no browser global. That is deliberate and load-bearing
 * in three ways:
 *
 *  1. The Vike SSR build imports the client transport on the server. A module that evaluates a
 *     browser-only global at import time takes the whole route down with it (T-38-74), and the
 *     cheapest guarantee against that is a module that has nothing to evaluate.
 *  2. It makes the one hand-rolled-SSE bug that matters testable in a plain node environment. A
 *     `ReadableStream` chunk has NO relationship to a frame boundary: a single `data:` line can
 *     arrive in two pieces and the blank-line terminator can straddle two reads. That is why
 *     `residual` below is a named field of the closure rather than a local variable.
 *  3. Nothing here parses JSON. A hostile or truncated payload therefore cannot break framing,
 *     because framing never looks inside the payload. Decoding is the CALLER's job, inside its own
 *     per-frame try/catch, so one bad frame is skipped rather than fatal (T-38-68).
 *
 * The caller decodes bytes to a string first (streaming decode, so a multi-byte codepoint split
 * across two reads is reassembled before it ever gets here). This parser is therefore purely
 * string-in / frames-out and is byte-agnostic by construction.
 *
 * Reference for the wire it reads: `chat-agent/src/channels/webTransport.ts` writes
 * `id: <bufferIndex>\ndata: <json>\n\n` for a buffered frame, `data: <json>\n\n` for an unbuffered
 * one, and `: hb\n\n` for the heartbeat.
 */

/**
 * One parsed frame. `id` is the server's buffer index, which is what a resume sends back as
 * `Last-Event-ID`; it is absent on an unbuffered frame (a refusal, or a frame written after the
 * run's byte cap tripped) and on a frame whose `id:` field could not be read as a finite number.
 * `data` is the raw field value, undecoded.
 */
export type ParsedFrame = { id?: number; data: string };

/** The stateful reader. One instance per stream; never share one across two responses. */
export type SseFrameParser = {
  /** Feed one decoded chunk. Returns every frame that COMPLETED in this chunk, in order. */
  push: (chunk: string) => ParsedFrame[];
  /** How much of an incomplete frame is still held. Diagnostics only; the caller never needs it. */
  pending: () => number;
};

const ID_FIELD = 'id:';
const DATA_FIELD = 'data:';
const COMMENT_PREFIX = ':';

/**
 * The three block terminators the SSE grammar allows. The server writes `\n\n`, but an
 * intermediary that normalises line endings would produce the CRLF form, and the cost of
 * accepting all three is one loop rather than a class of stream that silently never terminates a
 * frame. They cannot tie: a match at a given index fixes the character at that index, and the
 * three patterns disagree on it.
 */
const TERMINATORS = ['\n\n', '\r\n\r\n', '\r\r'] as const;

type Boundary = { index: number; length: number };

/** The earliest block terminator in `buffer`, or `null` while the block is still incomplete. */
const findBoundary = (buffer: string): Boundary | null => {
  let found: Boundary | null = null;
  for (const terminator of TERMINATORS) {
    const index = buffer.indexOf(terminator);
    if (index === -1) continue;
    if (found === null || index < found.index) {
      found = { index, length: terminator.length };
    }
  }
  return found;
};

/**
 * Strip the single optional space after a field's colon, per the SSE grammar. Only ONE space is
 * stripped: `data:  x` carries a value of `' x'`, and silently trimming it would corrupt a delta
 * whose text legitimately begins with a space.
 */
const stripFieldSpace = (value: string): string =>
  value.startsWith(' ') ? value.slice(1) : value;

/**
 * Read one complete block. Returns `null` when the block carries no `data:` field at all, which
 * covers a stray blank line and a block of pure metadata. Never throws: a malformed `id:` yields
 * an absent id rather than an exception, because a client that cannot read one cursor is still a
 * client that can read the answer.
 */
const parseBlock = (block: string): ParsedFrame | null => {
  let id: number | undefined;
  let data: string | undefined;

  for (const rawLine of block.split('\n')) {
    // A CRLF stream leaves the `\r` on the end of every line once split on `\n`.
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;

    if (line.startsWith(ID_FIELD)) {
      const raw = stripFieldSpace(line.slice(ID_FIELD.length)).trim();
      // `Number('')` is 0, which would be a real cursor invented out of an empty field. An empty
      // or unparseable id is absent, not zero.
      const parsed = raw.length === 0 ? Number.NaN : Number(raw);
      id = Number.isFinite(parsed) ? parsed : undefined;
      continue;
    }

    if (line.startsWith(DATA_FIELD)) {
      const value = stripFieldSpace(line.slice(DATA_FIELD.length));
      // The grammar allows several `data:` lines per block, joined with a newline. The server
      // writes one (its payload is `JSON.stringify` output, which contains no raw newline), but
      // joining rather than overwriting is what the spec says and costs nothing.
      data = data === undefined ? value : `${data}\n${value}`;
    }
  }

  if (data === undefined) return null;
  return { id, data };
};

/**
 * Create a frame reader with its own residual buffer.
 *
 * A block beginning with `:` is a comment. The server's heartbeat is exactly that, so a comment
 * emits nothing AND advances no cursor: if a heartbeat could touch the id the client remembers, a
 * resume after an idle panel would ask the server to replay from a frame that never existed.
 */
export const createSseFrameParser = (): SseFrameParser => {
  // THE point of this module. A frame split across two reads survives here and nowhere else.
  let residual = '';

  return {
    push: (chunk: string): ParsedFrame[] => {
      const frames: ParsedFrame[] = [];
      if (typeof chunk !== 'string' || chunk.length === 0) return frames;

      residual += chunk;

      for (;;) {
        const boundary = findBoundary(residual);
        if (boundary === null) break;

        const block = residual.slice(0, boundary.index);
        residual = residual.slice(boundary.index + boundary.length);

        if (block.startsWith(COMMENT_PREFIX)) continue;

        const frame = parseBlock(block);
        if (frame !== null) frames.push(frame);
      }

      return frames;
    },

    pending: (): number => residual.length,
  };
};
